/**
 * Unified export panel — handles both single-chat and bulk export views.
 *
 * The `mode` prop drives which info section is shown. The shared shell
 * (progress bar / export button, PreviewSection) is identical in both modes.
 */
import React, { useCallback, useRef } from 'react';
import {
	ExportOptions,
	ExportFormat,
	ExportTheme,
	HistoryModel,
} from '../../../types/models';
import { ExportState, PreviewState } from '../../../stores/chatManagementStore';
import {
	ExportFormatSelector,
	ExportThemeSelector,
} from '../../settings/ExportSettings';
import { formatChatDate } from '../CMShared';
import { ExportConfigBar } from './ExportConfigBar';
import { PreviewSection, PreviewSectionHandle } from './PreviewSection';

// ─── Shared sub-components ────────────────────────────────────────────────────

function ExportProgress({
	current,
	total,
	onCancel,
}: {
	current: number;
	total: number;
	onCancel: () => void;
}) {
	const percentage = Math.round((current / total) * 100);

	return (
		<div className="space-y-sm">
			<div className="flex items-center justify-center text-neutral-300">
				<i className="fas fa-spinner fa-spin mr-xs" />
				Exporting...
			</div>
			<div className="space-y-xs">
				<div className="flex justify-between items-center text-xs">
					<span className="text-neutral-300">Progress:</span>
					<span className="text-neutral-200 font-medium">
						{percentage}%
					</span>
				</div>
				<div className="w-full bg-neutral-600 rounded-full h-2">
					<div
						className="bg-red-500 h-2 rounded-full transition-all duration-300"
						style={{ width: `${percentage}%` }}
					/>
				</div>
			</div>
			<button
				onClick={onCancel}
				className="w-full btn-secondary"
				type="button"
			>
				Cancel Export
			</button>
		</div>
	);
}

// ─── Single mode info section ─────────────────────────────────────────────────

function SingleInfoSection({
	chat,
	messageCount,
	imageCount,
	isLoadingMessages,
	options,
	onOptionsChange,
}: {
	chat: HistoryModel;
	messageCount: number | null;
	imageCount: number | null;
	isLoadingMessages: boolean;
	options: ExportOptions;
	onOptionsChange: (options: ExportOptions) => void;
}) {
	const handleFormatChange = useCallback(
		(format: ExportFormat) => onOptionsChange({ ...options, format }),
		[options, onOptionsChange],
	);
	const handleThemeChange = useCallback(
		(theme: ExportTheme) => onOptionsChange({ ...options, theme }),
		[options, onOptionsChange],
	);

	return (
		<div className="bg-neutral-500 rounded-small p-md border border-neutral-400">
			<div className="space-y-sm text-sm">
				<div className="flex justify-between">
					<span className="text-neutral-200">Chat:</span>
					<span className="text-neutral-100 font-medium truncate max-w-[200px]">
						{chat.title}
					</span>
				</div>
				<div className="flex justify-between">
					<span className="text-neutral-200">Messages:</span>
					<span className="text-neutral-100 font-medium">
						{isLoadingMessages
							? 'Loading...'
							: (messageCount ?? 'Unknown')}
					</span>
				</div>
				<div className="flex justify-between">
					<span className="text-neutral-200">Images:</span>
					<span className="text-neutral-100 font-medium">
						{isLoadingMessages ? 'Loading...' : (imageCount ?? 0)}
					</span>
				</div>
				<div className="flex justify-between">
					<span className="text-neutral-200">Created:</span>
					<span className="text-neutral-100 font-medium">
						{formatChatDate(chat.created_at)}
					</span>
				</div>
				<div className="flex justify-between">
					<span className="text-neutral-200">Last Updated:</span>
					<span className="text-neutral-100 font-medium">
						{formatChatDate(chat.last_updated)}
					</span>
				</div>
				<div className="flex justify-between pb-sm flex-wrap">
					<ExportFormatSelector
						value={options.format}
						onChange={handleFormatChange}
					/>
					{options.format !== 'magazine' && (
					<div className="flex flex-row gap-xs items-center">
						{options.format !== 'pdf' && (
							<span className="max-w-[200px] text-neutral-300 text-xs justify-self-end text-right overflow-hidden">
								Used for preview only
							</span>
						)}
						<ExportThemeSelector
							value={options.theme}
							onChange={handleThemeChange}
						/>
					</div>
					)}
				</div>
			</div>
			<ExportConfigBar
				options={options}
				onOptionsChange={onOptionsChange}
			/>
		</div>
	);
}

// ─── Bulk mode info section ───────────────────────────────────────────────────

function BulkInfoSection({
	selectedCount,
	options,
	focusedChatTitle,
	isFocusedChatChecked,
	onAddToExport,
	onRemoveFromExport,
	onExportOnly,
}: {
	selectedCount: number;
	options: ExportOptions;
	// eslint-disable-next-line react/require-default-props
	focusedChatTitle?: string;
	isFocusedChatChecked: boolean;
	onAddToExport: () => void;
	onRemoveFromExport: () => void;
	onExportOnly: () => void;
}) {
	return (
		<>
			<div className="bg-neutral-500 rounded-small p-md border border-neutral-400">
				<div className="space-y-sm text-sm">
					<div className="flex justify-between">
						<span className="text-neutral-200">
							Selected chats:
						</span>
						<span className="text-neutral-100 font-medium">
							{selectedCount}
						</span>
					</div>
					<div className="flex justify-between">
						<span className="text-neutral-200">Format:</span>
						<span className="text-neutral-100 font-medium uppercase">
							{options.format === 'magazine' ? 'Magazine (.mag)' : options.format}
						</span>
					</div>
					{options.format === 'pdf' && (
						<div className="flex justify-between">
							<span className="text-neutral-200">Theme:</span>
							<span className="text-neutral-100 font-medium capitalize">
								{options.theme}
							</span>
						</div>
					)}
				</div>
			</div>

			{focusedChatTitle && options.format !== 'magazine' && (
				<div className="space-y-sm">
					<div className="flex items-center justify-between">
						<span className="text-sm text-neutral-200">
							Previewing:{' '}
							<span className="text-neutral-100 font-medium">
								&ldquo;{focusedChatTitle}&rdquo;
							</span>
							{isFocusedChatChecked && (
								<span className="text-xs text-red-400 ml-xs">
									(included)
								</span>
							)}
						</span>
					</div>
					<div className="flex gap-xs">
						{isFocusedChatChecked ? (
							<button
								onClick={onRemoveFromExport}
								className="btn-secondary text-xs"
								type="button"
							>
								<i className="fas fa-minus mr-xxs" />
								Remove from export
							</button>
						) : (
							<button
								onClick={onAddToExport}
								className="btn-secondary text-xs"
								type="button"
							>
								<i className="fas fa-plus mr-xxs" />
								Add to export
							</button>
						)}
						<button
							onClick={onExportOnly}
							className="btn-secondary text-xs"
							type="button"
						>
							<i className="fas fa-download mr-xxs" />
							Export only this chat
						</button>
					</div>
				</div>
			)}
		</>
	);
}

// ─── Public component ─────────────────────────────────────────────────────────

interface ExportPanelBaseProps {
	options: ExportOptions;
	exportState: ExportState;
	previewState: PreviewState;
	onExport: () => void;
	onCancel: () => void;
}

interface SingleExportPanelProps extends ExportPanelBaseProps {
	chat: HistoryModel;
	messageCount: number | null;
	imageCount: number | null;
	isLoadingMessages: boolean;
	onOptionsChange: (options: ExportOptions) => void;
}

interface BulkExportPanelProps extends ExportPanelBaseProps {
	selectedCount: number;
	// eslint-disable-next-line react/require-default-props
	focusedChatTitle?: string;
	isFocusedChatChecked: boolean;
	onAddToExport: () => void;
	onRemoveFromExport: () => void;
	onExportOnly: () => void;
}

type ExportPanelProps =
	| ({ mode: 'single' } & SingleExportPanelProps)
	| ({ mode: 'bulk' } & BulkExportPanelProps);

// ─── Shared panel shell ───────────────────────────────────────────────────────

function ExportPanelShell({
	mode,
	exportState,
	previewState,
	selectedCount,
	infoSection,
	onExport,
	onCancel,
	showPreview,
}: {
	mode: 'single' | 'bulk';
	exportState: ExportState;
	previewState: PreviewState;
	selectedCount: number;
	infoSection: React.ReactNode;
	onExport: () => void;
	onCancel: () => void;
	showPreview: boolean;
}) {
	const previewRef = useRef<PreviewSectionHandle | null>(null);

	const handleExportClick = useCallback(() => {
		previewRef.current?.collapse();
		onExport();
	}, [onExport]);

	const isSingleExporting = mode === 'single' && exportState.isExporting;

	return (
		<div className="space-y-md">
			{infoSection}

			{exportState.isExporting ? (
				<ExportProgress
					current={exportState.progress.current}
					total={exportState.progress.total}
					onCancel={onCancel}
				/>
			) : (
				<button
					onClick={handleExportClick}
					disabled={isSingleExporting}
					className={`w-full px-6 py-2 rounded-small text-sm font-medium transition-colors duration-200 ${
						isSingleExporting
							? 'bg-neutral-500 text-neutral-300 cursor-not-allowed'
							: 'bg-red-600 hover:bg-red-500 text-neutral-100'
					}`}
					type="button"
				>
					{isSingleExporting ? (
						<>
							<i className="fas fa-spinner fa-spin mr-xs" />
							Exporting...
						</>
					) : (
						<>
							<i className="fas fa-download mr-xs" />
							{mode === 'single'
								? 'Export Chat'
								: `Confirm Export (${selectedCount} chats)`}
						</>
					)}
				</button>
			)}

			{showPreview ? (
				<PreviewSection
					previewState={previewState}
					ref={previewRef}
					onScrollToBottom={() => previewRef.current?.scrollIntoView()}
				/>
			) : (
				<div className="bg-neutral-500 rounded-small p-sm border border-neutral-400">
					<div className="flex items-center justify-center h-32 text-neutral-400">
						<div className="text-center">
							<i className="fas fa-eye text-xl mb-xs" />
							<p className="text-xs">
								Select a chat to preview its export
							</p>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

// ─── Mode-specific wrappers ───────────────────────────────────────────────────

function SingleExportPanelInner({
	options,
	exportState,
	previewState,
	chat,
	messageCount,
	imageCount,
	isLoadingMessages,
	onOptionsChange,
	onExport,
	onCancel,
}: SingleExportPanelProps) {
	return (
		<ExportPanelShell
			mode="single"
			exportState={exportState}
			previewState={previewState}
			selectedCount={1}
			showPreview={options.format !== 'magazine'}
			onExport={onExport}
			onCancel={onCancel}
			infoSection={
				<SingleInfoSection
					chat={chat}
					messageCount={messageCount}
					imageCount={imageCount}
					isLoadingMessages={isLoadingMessages}
					options={options}
					onOptionsChange={onOptionsChange}
				/>
			}
		/>
	);
}

function BulkExportPanelInner({
	options,
	exportState,
	previewState,
	selectedCount,
	focusedChatTitle = undefined,
	isFocusedChatChecked,
	onAddToExport,
	onRemoveFromExport,
	onExportOnly,
	onExport,
	onCancel,
}: BulkExportPanelProps) {
	return (
		<ExportPanelShell
			mode="bulk"
			exportState={exportState}
			previewState={previewState}
			selectedCount={selectedCount}
			showPreview={!!focusedChatTitle && options.format !== 'magazine'}
			onExport={onExport}
			onCancel={onCancel}
			infoSection={
				<BulkInfoSection
					selectedCount={selectedCount}
					options={options}
					focusedChatTitle={focusedChatTitle}
					isFocusedChatChecked={isFocusedChatChecked}
					onAddToExport={onAddToExport}
					onRemoveFromExport={onRemoveFromExport}
					onExportOnly={onExportOnly}
				/>
			}
		/>
	);
}

// ─── Public router ────────────────────────────────────────────────────────────
// ExportPanelProps is a discriminated union on `mode`. After destructuring the
// shared base props, the remaining `rest` object is mode-specific but TypeScript
// can't narrow it through rest spreading. The `as` casts below are safe because
// the `mode` check guarantees the correct branch.

export function ExportPanel({
	mode,
	options,
	exportState,
	previewState,
	onExport,
	onCancel,
	...rest
}: ExportPanelProps) {
	if (mode === 'single') {
		const {
			chat,
			messageCount,
			imageCount,
			isLoadingMessages,
			onOptionsChange,
		} = rest as SingleExportPanelProps;
		return (
			<SingleExportPanelInner
				options={options}
				exportState={exportState}
				previewState={previewState}
				onExport={onExport}
				onCancel={onCancel}
				chat={chat}
				messageCount={messageCount}
				imageCount={imageCount}
				isLoadingMessages={isLoadingMessages}
				onOptionsChange={onOptionsChange}
			/>
		);
	}
	const {
		selectedCount,
		focusedChatTitle,
		isFocusedChatChecked,
		onAddToExport,
		onRemoveFromExport,
		onExportOnly,
	} = rest as BulkExportPanelProps;
	return (
		<BulkExportPanelInner
			options={options}
			exportState={exportState}
			previewState={previewState}
			onExport={onExport}
			onCancel={onCancel}
			selectedCount={selectedCount}
			focusedChatTitle={focusedChatTitle}
			isFocusedChatChecked={isFocusedChatChecked}
			onAddToExport={onAddToExport}
			onRemoveFromExport={onRemoveFromExport}
			onExportOnly={onExportOnly}
		/>
	);
}
